import {ofType, StateObservable} from "redux-observable-es6-compat";
import {Injectable} from "@angular/core";
import {AnalyticsActions, AnalyticsData} from "../Actions/analytics.actions";
import {ApplicationSpecActions} from "../Actions/application-spec-actions";
import {combineLatest, EMPTY, from, Observable, of} from "rxjs";
import {catchError, map, mapTo, mergeMap} from "rxjs/operators";
import {appProjectInfo} from "../../../info.app";
import {HttpClient} from "@angular/common/http";
import {ISelectorState} from "../selector.state";
import {ApplicationConfigActions} from "../Actions/application-config-actions";
import * as md5 from "md5";
import {Utils} from "../../common/utils";

// declare function _checkPolicyCookie(): boolean;
// declare function _gtag(...arg): void;
// declare function _adobeTrack(str: string, force: boolean);
// from analytics.js
declare var edsAnalytics: {
    init: (addScript, googleId) => Promise<void>,
    isInitialized: () => boolean,
    checkPolicyCookie:  () => boolean,
    adobeTrack: (str: string, force: boolean) => void,
    gtag: (...arg) => void,
    setDigitalData?: (type: string, category: string, shortName: string) => void
    onAnalyticsReady: () => Promise<void>
};

interface StromboliEvent {
  hit: string;
  module: string;
  topology: string;
  cookieID: string;
  cpn: string;
  rpn: string;
  details: any;
  user_id: string;
  tags: string;
}

export interface TTrackingData {
  cpn: string;
  details: string | Partial<{vin_min: number, vin_max: number, vout: number, iout: number, power: number}>;
  hit: string; // one of OPEN_DATASHEET | OPEN_PRODUCT_FOLDER | OPEN_SAMPLE_BUY
  module: string; // "dcdc"
  rpn?: string;
  tag?: string; // "evalboard"
  topology?: string; // "buck"
}

@Injectable()
export class AnalyticsEpics {

  constructor(private http: HttpClient) {
  }


  logInStromboli(event: string, data: AnalyticsData, store: ISelectorState): Observable<never> {

    // Aggiungere nel payload di Stromboli il campo 'cookieID' che conterrà un ID univoco che identifica la sessione dell'utente preso dal cookie 'cd_user_id'
    const cookieIDRow = document.cookie.split('; ').find((row) => row.startsWith('_mkto_trk='));

    const body: StromboliEvent = {
      hit: event.toUpperCase(),
      details: data.details,
      topology: data.topology ? data.topology : (store.appCatalog.selectedSolution ? store.appCatalog.selectedSolution.topology : ""),
      cookieID: cookieIDRow && cookieIDRow !== '' ? cookieIDRow.split(':')[2] : "",
      cpn: data.cpn ? data.cpn : (store.appCatalog.selectedSolution ? store.appCatalog.selectedSolution.CPN : ""),
      rpn: data.rpn ? data.rpn : (store.appCatalog.selectedSolution ? store.appCatalog.selectedSolution.RPN : ""),
      module: data.module ? data.module : (store.appCatalog.appClass ? store.appCatalog.appClass : "home"),
      user_id: store.appUserInfo.user ? store.appUserInfo.user.mail : "",
      tags: "console" + (data.tag ? ( ", " + data.tag) : "")
    };

    // let example;
    // example = {
    // }
    // ;
    // console.log(example)
    // const nB = {...body, event_date: new Date().toISOString()};
    // console.log(JSON.stringify(nB, null, 2));
    const d = new Date();
    const authorizationToken = md5(`${d.getUTCDay()}-${d.getUTCMonth() + 1}-${d.getUTCFullYear()}:${d.getUTCHours()}_stromboli_log`);
    if (edsAnalytics.checkPolicyCookie() && store.appConfig.edsLogStromboli) {
      return this.http.post("/api/log", body, {headers: {authorizationToken}}).pipe(
        mergeMap(_ => EMPTY),
        catchError(_ => EMPTY)
      );
    } else {
      return EMPTY;
    }
  }

  /*
  *
  * This function will set digital data and call the init function of analytics.
  * the init function will load analytics scripts if are not loaded else returns.
  * This function must be called before the edsAnalytics.gtag and the edsAnalytics.adobeTrack
  *
  * */
  initAnalytics(digitalData?: string, breadCrumb?: string[]): Observable<void> {
    if (digitalData) {
      const _urlArr: string[] = digitalData.replace(/\//g, ':').split(':').filter(x => x !== '');     // if selected payload is the url /app/${appClass}/${catalog}/filter/[]/selected/${cpn}:${topology}

      const typeV60 = _urlArr.length > 2
        ? (_urlArr[1].includes('led') ? "LED Driver" : "Converter")
        : 'eDesign';
      const categoryV60 = _urlArr.length > 2
        ? (_urlArr[1].includes('acdc') ? "ACDC" : "DCDC")
        : (_urlArr.length > 0 ? Utils.capitalize(_urlArr[_urlArr.length - 1]) : ''); // other cases ==> home, help and dashboard;

      const type = _urlArr.length > 2
        ? 'Power Management Design Center'
        : 'eDesign' + (_urlArr.length > 0 ? ':' + Utils.capitalize(_urlArr[_urlArr.length - 1]) : ''); // other cases ==> home, help and dashboard;
      const category = _urlArr.length > 2
        ? (_urlArr[1].includes('led') ? "LED Lighting Design Tools" : "Power Supply Design Tools")
        : '';  // (_urlArr.length > 0 ? _urlArr[_urlArr.length - 1] : ''); // other cases ==> home, help and dashboard no category;

      const selected = _urlArr.find(x => x === 'selected') ? (_urlArr.length > 3 ? _urlArr.slice(-3, -1).reverse() : []) : [];
      // const selected = _urlArr.find(x => x === 'selected') ? `${_urlArr.length > 3 ? _urlArr.slice(-3).reverse().join(':') : ''}` : '';
      // const selected = store.value.appCatalog.selectedSolution ? `${type}:${category}:${store.value.appCatalog.selectedSolution.topology}:${store.value.appCatalog.selectedSolution.CPN}:selected` : '';
      const breadCrump = (breadCrumb && (digitalData.endsWith('grid') || digitalData.includes('selected'))) ? breadCrumb.map(x => decodeURIComponent(x)) : [];

      const shortName: string[] = [
        typeV60,
        categoryV60,
        ...breadCrump,
        ...selected,
      ];
      // In error
      // type      V13 = 404 Error Page
      // category  V43 = 404 Error Page
      // shortName V60 = error:404

      // In home|help|dashboard:
      // type      V13 = eDesign
      // category  V43 = [home|help|dashboard]
      // shortName V60 = edesignsuite:[home|help|dashboard]
      //     Update for mail of 23/09/2021 "eDesign tracking review" new short name V60 in case of selected
      //     type      V13 = eDesign:[Home|Help|Dashboard]
      //     category  V43 = ''
      //     shortName V60 = edesign:[home|help|dashboard]

      // In Grid selection
      // type      V13 = [Power Management Design Center]
      // category  V43 = [LED Lighting Design Tools|Power Supply Design Tools]
      // shortName V60 = [LED|Converter]:[ACDC|DCDC]:${path_selected_breadcrumb}

      // In cpn selected
      // type      V13 = [Power Management Design Center]
      // category  V43 = [LED Lighting Design Tools|Power Supply Design Tools]
      // shortName V60 = [LED|Converter]:[ACDC|DCDC]:${Topology}:${CPN}:selected
      //     Update for mail of 23/09/2021 "eDesign tracking review" new short name V60 in case of selected
      //     shortName V60 = [LED|Converter]:[ACDC|DCDC]:${path_selected_breadcrumb}:${CPN}:selected
      if (edsAnalytics.setDigitalData) {
        if (digitalData.endsWith('error')) {
          edsAnalytics.setDigitalData("404 Error Page", "404 Error Page", "error:404");
        } else {
          edsAnalytics.setDigitalData(type, category, shortName.filter(x => x !== '').join(':').toLowerCase().replace(/\//g, ''));
        }
      }
    }

    return from(edsAnalytics.init(true, appProjectInfo.analyticsID));
  }

  sendPageInformation = (action$, store: StateObservable<ISelectorState>) => {
    const waitLoadConfig = action$.pipe(ofType(ApplicationConfigActions.ActionType.SET_CONFIG));
    const updatelocation = action$.pipe(ofType("@angular-redux/router::UPDATE_LOCATION"));
    return combineLatest([waitLoadConfig, updatelocation]).pipe(
      map(([x, y]) => y),
      mergeMap( (y: {payload: string}) => this.initAnalytics(y.payload, store.value.appProjectTypes.breadcrumb.map(x => x.label).slice(2)).pipe(mapTo(y))),
      mergeMap(({payload}) => {
        // console.log('Check file', payload, window.location.href);
        edsAnalytics.gtag('config', appProjectInfo.analyticsID, {
          'page_title': document.title,
          'page_location': window.location.href,
          'page_path': payload
        });

        const adobeStr = !payload.endsWith('#/error') ? "event-page-view" : "event-page-error";

        if (!payload.includes('widget/param')) {
          edsAnalytics.adobeTrack(adobeStr, false);
        }
        // return this.logInStromboli("load_page", {details: payload}, store.value); //Richiesta esplicita da parte di Patrizia
        return EMPTY;
      })
    );
  }

  sendEventInformation = (action$, store) => {
    const waitLoadConfig = action$.pipe(ofType(ApplicationConfigActions.ActionType.SET_CONFIG));
    const updatelocation = action$.pipe(ofType(AnalyticsActions.ActionType.SEND_EVENT));
    return combineLatest([waitLoadConfig, updatelocation]).pipe(
      map(([x, y]) => y),
      mergeMap( y => this.initAnalytics().pipe(mapTo(y))),
      mergeMap(({payload}) => {
        // console.log('Check file', payload, window.location.href);
        edsAnalytics.gtag('event', payload.name, {
          'send_to': appProjectInfo.analyticsID,
          'event_category': payload.category,
          'event_label': JSON.stringify(payload.data)
        });
        return this.logInStromboli(payload.name, payload.data, store.value);
      })
    );
  }

  sendException = (action$, store) => {
    return action$.pipe(
      ofType(AnalyticsActions.ActionType.SEND_EXCEPTION),
      mergeMap( y => this.initAnalytics().pipe(mapTo(y))),
      mergeMap(({payload}) => {
        // console.log('Check file', payload, window.location.href);
        edsAnalytics.gtag('event', 'exception', {
          'description': payload,
          'fatal': false
        });
        return this.logInStromboli("error", {details: payload}, store.value);
      })
    );
  }

  sendFeatureUpdate = (action$, store) => {
    return action$.pipe(
      ofType(ApplicationSpecActions.ActionType.UPDATE_FEATURE_FILTER),
      mergeMap( y => this.initAnalytics().pipe(mapTo(y))),
      mergeMap(({payload}) => {
        // console.log('Check file', payload, window.location.href);
        const state = (payload.stateFilter < 0) ? "Exclude" : ((payload.stateFilter > 0) ? "Include" : "No Filter");
        const data = {details: {feature: payload.longLabel, state}};
        edsAnalytics.gtag('event', 'feature_update', {
          'send_to': appProjectInfo.analyticsID,
          'event_category': 'feature',
          'event_label': JSON.stringify(data)
        });
        return this.logInStromboli("feature_update", data, store.value);
      })
    );
  }

}
